import React, { useState, useEffect } from 'react';
import { BanknotesIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const C = { teal:'#00B4D8', success:'#10B981', warning:'#F59E0B', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', bg:'#F9FAFB', navy:'#0D1B2A', error:'#EF4444' };

const METHOD_OPTIONS = [
  { value:'bank_transfer', label:'🏦 Bank Transfer', desc:'Receive payment directly to your bank account' },
  { value:'paypal', label:'🅿️ PayPal', desc:'Receive payment via PayPal' },
  { value:'wise', label:'💸 Wise (TransferWise)', desc:'Receive payment via Wise — great for international transfers' },
];

const inputStyle = { width:'100%', padding:'10px 14px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, outline:'none', boxSizing:'border-box', color:C.dark, background:'#fff' };
const labelStyle = { display:'block', fontSize:12, fontWeight:600, color:C.grey, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:6 };

function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom:16 }}>
      <label style={labelStyle}>{label}{required && <span style={{ color:C.error }}> *</span>}</label>
      {children}
    </div>
  );
}

export default function TutorPaymentSettings() {
  const [form, setForm] = useState({
    payment_method: 'bank_transfer',
    bank_name: '', account_holder: '', account_number: '',
    sort_code: '', swift_bic: '', paypal_email: '', wise_email: '', payment_notes: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasInfo, setHasInfo] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    api.get('/api/tutor/payment-info').then(r => {
      setForm({
        payment_method: r.data.payment_method || 'bank_transfer',
        bank_name: r.data.bank_name || '',
        account_holder: r.data.account_holder || '',
        account_number: r.data.account_number || '',
        sort_code: r.data.sort_code || '',
        swift_bic: r.data.swift_bic || '',
        paypal_email: r.data.paypal_email || '',
        wise_email: r.data.wise_email || '',
        payment_notes: r.data.payment_notes || '',
      });
      setHasInfo(r.data.has_payment_info);
      setUpdatedAt(r.data.updated_at);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    const method = form.payment_method;
    if (method === 'bank_transfer' && (!form.account_holder || !form.account_number)) {
      toast.error('Please enter account holder name and account number / IBAN');
      return;
    }
    if (method === 'paypal' && !form.paypal_email) {
      toast.error('Please enter your PayPal email address');
      return;
    }
    if (method === 'wise' && !form.wise_email) {
      toast.error('Please enter your Wise email address');
      return;
    }
    setSaving(true);
    try {
      await api.patch('/api/tutor/payment-info', form);
      setHasInfo(true);
      setUpdatedAt(new Date().toISOString());
      toast.success('Payment details saved!');
    } catch { toast.error('Failed to save. Please try again.'); }
    setSaving(false);
  };

  if (loading) return <Layout role="tutor"><div style={{ textAlign:'center', padding:60, color:C.grey }}>Loading...</div></Layout>;

  const method = form.payment_method;

  return (
    <Layout role="tutor">
      <div style={{ maxWidth:620 }}>
        {/* Header */}
        <div style={{ marginBottom:24 }}>
          <h2 style={{ margin:'0 0 6px', fontSize:22, fontWeight:800, color:C.navy }}>Payment Settings</h2>
          <p style={{ margin:0, fontSize:14, color:C.grey }}>Your payment details are shared with your school admin for monthly payouts.</p>
        </div>

        {/* Status banner */}
        {!hasInfo ? (
          <div style={{ background:'#FEF3C7', border:'1px solid #FCD34D', borderRadius:10, padding:14, marginBottom:20, display:'flex', alignItems:'center', gap:10 }}>
            <ExclamationTriangleIcon style={{ width:20, height:20, color:'#D97706', flexShrink:0 }} />
            <div style={{ fontSize:13, color:'#92400E' }}>
              <strong>Action needed:</strong> Please add your payment details so the school can send your monthly payout.
            </div>
          </div>
        ) : (
          <div style={{ background:'#D1FAE5', border:'1px solid #6EE7B7', borderRadius:10, padding:14, marginBottom:20, display:'flex', alignItems:'center', gap:10 }}>
            <CheckCircleIcon style={{ width:20, height:20, color:'#059669', flexShrink:0 }} />
            <div style={{ fontSize:13, color:'#065F46' }}>
              <strong>Payment details saved.</strong>{updatedAt && ` Last updated ${new Date(updatedAt).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' })}.`}
            </div>
          </div>
        )}

        {/* Payout schedule info */}
        <div style={{ background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:10, padding:16, marginBottom:24, fontSize:13, color:'#1D4ED8' }}>
          <div style={{ fontWeight:700, marginBottom:4 }}>📅 Payout Schedule</div>
          <div>Payouts are processed by your school on the <strong>5th of every month</strong>. Make sure your payment details are up to date before then.</div>
        </div>

        <div className="card" style={{ padding:28 }}>
          {/* Payment method selector */}
          <div style={{ marginBottom:24 }}>
            <label style={labelStyle}>Payment Method</label>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {METHOD_OPTIONS.map(opt => (
                <label key={opt.value} onClick={() => set('payment_method', opt.value)} style={{
                  display:'flex', alignItems:'center', gap:14, padding:'14px 16px',
                  border:`2px solid ${method === opt.value ? C.teal : C.border}`,
                  borderRadius:10, cursor:'pointer',
                  background: method === opt.value ? `${C.teal}08` : '#fff',
                  transition:'all 0.15s',
                }}>
                  <div style={{
                    width:18, height:18, borderRadius:'50%', flexShrink:0,
                    border:`2px solid ${method === opt.value ? C.teal : C.grey}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    {method === opt.value && <div style={{ width:8, height:8, borderRadius:'50%', background:C.teal }} />}
                  </div>
                  <div>
                    <div style={{ fontWeight:600, fontSize:14, color:C.dark }}>{opt.label}</div>
                    <div style={{ fontSize:12, color:C.grey }}>{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Bank Transfer fields */}
          {method === 'bank_transfer' && (
            <>
              <Field label="Account Holder Name" required>
                <input style={inputStyle} value={form.account_holder} onChange={e => set('account_holder', e.target.value)} placeholder="Full name as shown on account" />
              </Field>
              <Field label="Bank Name" required>
                <input style={inputStyle} value={form.bank_name} onChange={e => set('bank_name', e.target.value)} placeholder="e.g. Barclays, HSBC, Kapitalbank" />
              </Field>
              <Field label="Account Number / IBAN" required>
                <input style={inputStyle} value={form.account_number} onChange={e => set('account_number', e.target.value)} placeholder="e.g. GB29 NWBK 6016 1331 9268 19" />
              </Field>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:16 }}>
                <Field label="Sort Code (if UK)">
                  <input style={inputStyle} value={form.sort_code} onChange={e => set('sort_code', e.target.value)} placeholder="e.g. 20-00-00" />
                </Field>
                <Field label="SWIFT / BIC (international)">
                  <input style={inputStyle} value={form.swift_bic} onChange={e => set('swift_bic', e.target.value)} placeholder="e.g. NWBKGB2L" />
                </Field>
              </div>
            </>
          )}

          {/* PayPal fields */}
          {method === 'paypal' && (
            <Field label="PayPal Email Address" required>
              <input style={inputStyle} type="email" value={form.paypal_email} onChange={e => set('paypal_email', e.target.value)} placeholder="your@paypal.com" />
            </Field>
          )}

          {/* Wise fields */}
          {method === 'wise' && (
            <Field label="Wise Email Address" required>
              <input style={inputStyle} type="email" value={form.wise_email} onChange={e => set('wise_email', e.target.value)} placeholder="your@wise.com" />
            </Field>
          )}

          {/* Notes */}
          <Field label="Additional Notes (optional)">
            <textarea style={{ ...inputStyle, minHeight:72, resize:'vertical', fontFamily:'inherit' }}
              value={form.payment_notes}
              onChange={e => set('payment_notes', e.target.value)}
              placeholder="Any additional info for the school admin (e.g. preferred currency, reference to use)" />
          </Field>

          <button onClick={handleSave} disabled={saving} style={{
            width:'100%', padding:'13px 0', background: C.teal, color:'#fff',
            border:'none', borderRadius:10, fontWeight:700, fontSize:15, cursor:'pointer', opacity: saving ? 0.7 : 1,
          }}>
            <BanknotesIcon style={{ width:18, height:18, display:'inline', marginRight:8, verticalAlign:'middle' }} />
            {saving ? 'Saving...' : 'Save Payment Details'}
          </button>
        </div>

        {/* Privacy note */}
        <div style={{ marginTop:16, fontSize:12, color:C.grey, textAlign:'center', lineHeight:1.6 }}>
          🔒 Your payment details are only shared with your school admin for the purpose of processing your monthly payout.
        </div>
      </div>
    </Layout>
  );
}
